import { Delete, Edit, Fullscreen, FullscreenExit, Send } from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Paper,
  Tooltip,
  Typography,
  Button,
  styled,
} from "@mui/material";
import { useNavigation } from "contexts/notificationsContext";
import { useAuth } from "shared/hooks/auth";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
// Importa o quill-mention - ele se auto-registra ao ser importado
import "quill-mention";
import "quill-mention/dist/quill.mention.css";
import "./quill-mention.css";
import {
  atualizarComentario,
  criarComentario,
  excluirComentario,
  IComentario,
  listarComentarios,
  listarComentariosSubtarefa,
} from "services/modules/tarefaDetalhe";
import {
  getResponsaveisFiltro,
  IResponsavelFiltro,
} from "services/modules/quadroTarefas";

// ============================================
// DEBUG: Verificar registro do módulo quill-mention
// ============================================
console.log("[PainelComentarios] Quill:", Quill);
console.log("[PainelComentarios] Quill.imports:", (Quill as any).imports);
console.log("[PainelComentarios] modules/mention registrado:", !!(Quill as any).imports?.["modules/mention"]);

// ============================================
// STYLED COMPONENTS - Conforme Mockup
// ============================================

const EditorContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFFDE7", // Fundo amarelo claro conforme mockup
  borderRadius: 8,
  padding: theme.spacing(2),
  position: "relative",
  "& .ql-toolbar": {
    backgroundColor: "#fff",
    borderRadius: "4px 4px 0 0",
    border: "1px solid #ccc",
    borderBottom: "none",
  },
  "& .ql-container": {
    backgroundColor: "#fff",
    borderRadius: "0 0 4px 4px",
    border: "1px solid #ccc",
    minHeight: 100,
  },
  "& .ql-editor": {
    minHeight: 100,
    fontSize: "0.9rem",
  },
  // Estilos para menções inline
  "& .mention": {
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
    borderRadius: 4,
    padding: "2px 4px",
    fontWeight: 500,
  },
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 1,
  backgroundColor: "#fff",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));

const ComentarioContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  backgroundColor: "#FFFDE7", // Fundo amarelo claro conforme mockup
  borderRadius: 8,
  border: "1px solid #E0E0E0",
}));

const ComentarioHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));

const ComentarioTexto = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: 4,
  padding: theme.spacing(1.5),
  border: "1px solid #E0E0E0",
  fontSize: "0.875rem",
  lineHeight: 1.5,
  "& p": {
    margin: 0,
  },
  "& ul, & ol": {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
  },
  // Estilos para menções dentro do texto do comentário
  "& .mention": {
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
    borderRadius: 4,
    padding: "2px 4px",
    fontWeight: 500,
  },
}));

// ============================================
// INTERFACES
// ============================================

interface PainelComentariosProps {
  idTarefa: number;
  onRefresh: () => void;
  isSubtarefa?: boolean; // Indica se está em uma sub-tarefa
  // N13.3: referência da tarefa/contexto (responsáveis cross-empresa nas menções)
  idReferencia?: number | null;
}

// ============================================
// COMPONENTE
// ============================================

export const PainelComentarios = ({
  idTarefa,
  onRefresh,
  isSubtarefa = false,
  idReferencia,
}: PainelComentariosProps) => {
  const { openNotification } = useNavigation();
  const { user } = useAuth();

  // Estados
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [comentarios, setComentarios] = useState<IComentario[]>([]);
  const [novoComentario, setNovoComentario] = useState("");
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [textoEdicao, setTextoEdicao] = useState("");
  const [editorExpandido, setEditorExpandido] = useState(false);
  const [usuarios, setUsuarios] = useState<IResponsavelFiltro[]>([]);

  // Referência para os editores Quill
  const quillRef = useRef<ReactQuill>(null);
  const quillExpandidoRef = useRef<ReactQuill>(null);
  // Referência para a lista de usuários (usada pelo módulo de menções)
  const usuariosRef = useRef<IResponsavelFiltro[]>([]);

  // Carrega lista de usuários para menções
  useEffect(() => {
    const carregarUsuarios = async () => {
      console.log("[PainelComentarios] Carregando usuários para menções. idEmpresa:", user?.idEmpresa);
      if (user?.idEmpresa) {
        try {
          const data = await getResponsaveisFiltro(user.idEmpresa, idReferencia);
          console.log("[PainelComentarios] Usuários carregados:", data.length, "usuários");
          setUsuarios(data);
          usuariosRef.current = data; // Atualiza a referência
        } catch (error) {
          console.error("Erro ao carregar usuários para menções:", error);
        }
      } else {
        console.warn("[PainelComentarios] idEmpresa não disponível para carregar usuários");
      }
    };
    carregarUsuarios();
  }, [user?.idEmpresa, idReferencia]);

  // Sincroniza a referência quando usuários mudam
  useEffect(() => {
    usuariosRef.current = usuarios;
  }, [usuarios]);

  // Debug: Verifica se o módulo Mention foi inicializado corretamente no editor
  useEffect(() => {
    const checkMentionModule = () => {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        console.log("[PainelComentarios] Editor Quill obtido:", editor);
        
        // Verifica se o módulo mention existe
        const mentionModule = editor.getModule("mention");
        console.log("[PainelComentarios] Módulo mention no editor:", mentionModule);
        
        if (mentionModule) {
          console.log("[PainelComentarios] Opções do módulo mention:", mentionModule.options);
          console.log("[PainelComentarios] Source function:", mentionModule.options?.source);
        }
      }
    };
    
    // Aguarda um pouco para o editor estar pronto
    const timer = setTimeout(checkMentionModule, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Configuração do módulo Quill com menções
  // IMPORTANTE: Os módulos devem ser estáticos para o ReactQuill funcionar corretamente
  // Por isso usamos uma ref para acessar a lista de usuários atualizada
  const quillModules = useMemo(
    () => {
      console.log("[PainelComentarios] Criando quillModules");
      
      const mentionConfig = {
        allowedChars: /^[A-Za-zÀ-ÿ\s]*$/, // Permite caracteres acentuados
        mentionDenotationChars: ["@"],
        showDenotationChar: true,
        spaceAfterInsert: true,
        defaultMenuOrientation: "bottom",
        dataAttributes: ["id", "value"],
        minChars: 0, // Mostra lista ao digitar apenas "@"
        maxChars: 31,
        isolateCharacter: false,
        allowInlineMentionChar: true, // Permite @ no meio de palavras
        source: function (
          searchTerm: string,
          renderList: (matches: any[], searchTerm: string) => void,
          mentionChar: string
        ) {
          console.log("[Mention] Source chamada! searchTerm:", searchTerm, "mentionChar:", mentionChar);
          // Usa a referência para obter a lista atualizada de usuários
          const usuariosAtuais = usuariosRef.current;
          console.log("[Mention] Usuários disponíveis:", usuariosAtuais.length);
          
          // Mapeia os usuários para o formato esperado pelo quill-mention
          const usuariosFormatados = usuariosAtuais.map((u) => ({
            id: u.idUsuario,
            value: u.nome,
          }));
          console.log("[Mention] Usuários formatados:", usuariosFormatados.length);

          if (searchTerm.length === 0) {
            console.log("[Mention] Renderizando todos os usuários");
            renderList(usuariosFormatados, searchTerm);
          } else {
            const matches = usuariosFormatados.filter((item) =>
              item.value.toLowerCase().includes(searchTerm.toLowerCase())
            );
            console.log("[Mention] Matches encontrados:", matches.length);
            renderList(matches, searchTerm);
          }
        },
        renderItem: function (item: { id: number; value: string }) {
          return `<span class="mention-item">${item.value}</span>`;
        },
        onOpen: function () {
          console.log("[Mention] Menu aberto!");
          return true;
        },
        onClose: function () {
          console.log("[Mention] Menu fechado!");
          return true;
        },
      };
      
      console.log("[PainelComentarios] mentionConfig criado:", Object.keys(mentionConfig));
      
      return {
        toolbar: [
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ color: [] }, { background: [] }],
          ["link"],
          ["clean"],
        ],
        mention: mentionConfig,
      };
    },
    [] // Sem dependências - os módulos são estáticos, usamos ref para dados dinâmicos
  );

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
    "mention", // Adiciona formato de menção
  ];

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================

  const carregarComentarios = useCallback(async () => {
    setIsLoading(true);
    try {
      // Quando é sub-tarefa, o idTarefa passado é na verdade o idSubtarefa
      const data = isSubtarefa 
        ? await listarComentariosSubtarefa(idTarefa)
        : await listarComentarios(idTarefa);
      setComentarios(data);
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao carregar comentários",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [idTarefa, isSubtarefa, openNotification]);

  useEffect(() => {
    carregarComentarios();
  }, [carregarComentarios]);

  // ============================================
  // HANDLERS
  // ============================================

  // Verifica se o conteúdo do editor está vazio
  const isEditorVazio = (html: string): boolean => {
    // Remove tags HTML e espaços em branco
    const textoLimpo = html.replace(/<[^>]*>/g, "").trim();
    return textoLimpo === "";
  };

  // Extrai os IDs dos usuários mencionados a partir dos <span class="mention" data-id="...">
  // inseridos pelo quill-mention no HTML do comentário (item 6.2 — notificação por e-mail).
  const extrairIdsMencionados = (html: string): number[] => {
    const ids = new Set<number>();
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      doc.querySelectorAll("span.mention").forEach((span) => {
        const raw = span.getAttribute("data-id");
        const id = Number(raw);
        if (raw != null && raw !== "" && Number.isFinite(id) && id > 0) {
          ids.add(id);
        }
      });
    } catch (error) {
      console.error("[PainelComentarios] Erro ao extrair menções:", error);
    }
    return Array.from(ids);
  };

  const handleEnviar = async () => {
    if (isEditorVazio(novoComentario)) return;

    setIsSending(true);
    try {
      const idsUsuariosMencionados = extrairIdsMencionados(novoComentario);
      // Quando é sub-tarefa, o idTarefa passado é na verdade o idSubtarefa
      await criarComentario({
        idTarefa: isSubtarefa ? undefined : idTarefa,
        idSubtarefa: isSubtarefa ? idTarefa : undefined,
        comentario: novoComentario,
        idsUsuariosMencionados: idsUsuariosMencionados.length
          ? idsUsuariosMencionados
          : undefined,
      });
      setNovoComentario("");
      await carregarComentarios();
      openNotification({
        isOpen: true,
        message: "Comentário adicionado!",
        type: "success",
      });
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao enviar comentário",
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleEditar = (comentario: IComentario) => {
    setEditandoId(comentario.idTarefaComentario);
    // Usa textoHtml se disponível, senão usa texto
    setTextoEdicao(comentario.textoHtml || comentario.texto);
  };

  const handleSalvarEdicao = async () => {
    if (!editandoId || isEditorVazio(textoEdicao)) return;

    setIsSending(true);
    try {
      await atualizarComentario({
        idTarefaComentario: editandoId,
        comentario: textoEdicao,
      });
      setEditandoId(null);
      setTextoEdicao("");
      await carregarComentarios();
      openNotification({
        isOpen: true,
        message: "Comentário atualizado!",
        type: "success",
      });
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao atualizar comentário",
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCancelarEdicao = () => {
    setEditandoId(null);
    setTextoEdicao("");
  };

  const handleExcluir = async (idComentario: number) => {
    if (!window.confirm("Deseja excluir este comentário?")) return;

    try {
      await excluirComentario(idComentario);
      await carregarComentarios();
      openNotification({
        isOpen: true,
        message: "Comentário excluído!",
        type: "success",
      });
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao excluir comentário",
        type: "error",
      });
    }
  };

  // Função para formatar data conforme mockup: hora + dia da semana
  const formatarDataCompleta = (data: string): string => {
    const dataComentario = new Date(data);
    
    // Formata hora: HH:mm
    const hora = dataComentario.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    
    // Formata dia da semana
    const diasSemana = [
      "Domingo",
      "Segunda-feira",
      "Terça-feira",
      "Quarta-feira",
      "Quinta-feira",
      "Sexta-feira",
      "Sábado",
    ];
    const diaSemana = diasSemana[dataComentario.getDay()];
    
    return `${hora} - ${diaSemana}`;
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Lista de Comentários */}
      <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : comentarios.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary" variant="body2">
              Nenhum comentário ainda
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {comentarios.map((comentario) => (
              <ListItem
                key={comentario.idTarefaComentario}
                disablePadding
                sx={{ mb: 1, display: "block" }}
              >
                <ComentarioContainer elevation={0}>
                  {/* Cabeçalho do comentário */}
                  <ComentarioHeader>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.8rem",
                          bgcolor: "primary.main",
                        }}
                      >
                        {comentario.iniciaisUsuario}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {comentario.nomeUsuario}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatarDataCompleta(comentario.dataCriacao)}
                          {comentario.dataAtualizacao && " (editado)"}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Ações */}
                    <Box>
                      {comentario.podeEditar && editandoId !== comentario.idTarefaComentario && (
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditar(comentario)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {comentario.podeExcluir && (
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleExcluir(comentario.idTarefaComentario)
                            }
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </ComentarioHeader>

                  {/* Texto do comentário */}
                  {editandoId === comentario.idTarefaComentario ? (
                    <Box>
                      <ReactQuill
                        theme="snow"
                        value={textoEdicao}
                        onChange={setTextoEdicao}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Edite o comentário..."
                      />
                      <Box display="flex" gap={1} mt={1} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleCancelarEdicao}
                        >
                          Cancelar
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSalvarEdicao}
                          disabled={isSending || isEditorVazio(textoEdicao)}
                        >
                          {isSending ? <CircularProgress size={16} /> : "Salvar"}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <ComentarioTexto
                      dangerouslySetInnerHTML={{
                        __html: comentario.textoHtml || comentario.texto,
                      }}
                    />
                  )}
                </ComentarioContainer>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Editor de novo comentário */}
      <EditorContainer>
        <ExpandButton
          size="small"
          onClick={() => setEditorExpandido(true)}
          title="Expandir editor"
        >
          <Fullscreen fontSize="small" />
        </ExpandButton>
        
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={novoComentario}
          onChange={setNovoComentario}
          modules={quillModules}
          formats={quillFormats}
          placeholder="Digite seu comentário... (Use @ para mencionar usuários)"
        />
        
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button
            variant="contained"
            size="small"
            onClick={handleEnviar}
            disabled={isEditorVazio(novoComentario) || isSending}
            endIcon={isSending ? <CircularProgress size={16} /> : <Send />}
            sx={{ textTransform: "none" }}
          >
            Enviar
          </Button>
        </Box>
      </EditorContainer>

      {/* ====== MODAL EDITOR EXPANDIDO ====== */}
      <Dialog
        open={editorExpandido}
        onClose={() => setEditorExpandido(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Novo Comentário
          <IconButton
            onClick={() => setEditorExpandido(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <FullscreenExit />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <ReactQuill
              ref={quillExpandidoRef}
              theme="snow"
              value={novoComentario}
              onChange={setNovoComentario}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Digite seu comentário... (Use @ para mencionar usuários)"
              style={{ height: 300 }}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={6}>
            <Button
              variant="outlined"
              onClick={() => setEditorExpandido(false)}
              sx={{ mr: 1 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleEnviar();
                setEditorExpandido(false);
              }}
              disabled={isEditorVazio(novoComentario) || isSending}
              endIcon={isSending ? <CircularProgress size={16} /> : <Send />}
            >
              Enviar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
